<template>
  <l-map
    ref="map"
    @ready="onMapReady()"
    :zoom="zoom"
    :center="center"
    :options="state.mapOptions"
    v-on:click="mapClick"
    v-on:popupclose="state.geoJson = null"
  >
    <transition name="loading">
      <LoadingIndicator v-show="state.loading"></LoadingIndicator>
    </transition>
    <!-- Basemap -->
    <e-basemap-layer
      basemap="arcgis/light-gray/base"
      :apikey="esriKey"
    ></e-basemap-layer>
    <e-dynamic-map-layer
      :opacity="0.9"
      url="https://gis.chesapeakebay.net/ags/rest/services/Boundaries/watershed/MapServer"
      :use-cors="false"
    ></e-dynamic-map-layer>
    <e-dynamic-map-layer
      :opacity="state.layerOpacity / 100"
      :url="dataSource"
      :use-cors="false"
      :layers="layersProp.length > 0 ? layersProp.map(Number) : false"
      :from="overlayLayerTimeRange[0]"
      :to="overlayLayerTimeRange[1]"
      v-on:loading="state.loading = true"
      v-on:load="state.loading = false"
      ref="overlayLayer"
    ></e-dynamic-map-layer>
    <l-geo-json
      :geojson="state.geoJson"
      v-if="state.geoJson"
      :style="geoStyler"
      :options="state.geoJsonOptions"
    ></l-geo-json>
    <!-- Infowindow (set random rectangle to house popup) -->
    <l-rectangle
      :lat-lngs="[
        [41.734852, -86.809485],
        [41.734853, -86.809483],
        [41.734851, -86.809482],
        [41.73485, -86.809484],
      ]"
      :fill="false"
      :opacity="0"
      :stroke="false"
      ref="features"
    >
      <l-popup :content="state.popUpContent"></l-popup>
    </l-rectangle>
    <!-- Labels -->
    <e-basemap-layer
      :apikey="esriKey"
      basemap="arcgis/human-geography/labels"
      pane="labels"
    ></e-basemap-layer>
    <!-- Geocoder Control -->
    <e-control-geocoder
      :providers="state.geocoderProviders"
      :use-map-bounds="false"
      :expanded="true"
      :zoom-to-result="false"
      :search-bounds="state.usaSearchBounds"
      title="Search for a Location."
      placeholder="Type address or zipcode."
      position="bottomright"
      :collapse-after-result="false"
      v-on:results="geosearchOnResult"
    ></e-control-geocoder>
    <!-- Opacity Control -->
    <l-control
      position="topright"
      :disableClickPropagation="true"
      class="leaflet-bar leaflet-control leaflet-control-custom opacity-control"
      v-if="showOpacitySlider"
    >
      <div class="opacityBox">
        <label
          class="opacityBox__heading leaflet-control-custom__heading"
          for="opacitySlider"
          >Opacity</label
        >
        <div class="opacityBox__controls">
          <button
            style="margin-left: -5px"
            title="Decrease opacity"
            class="leaflet-control-zoom-in"
            v-on:click="updateOpacity(state.layerOpacity - 10)"
          >
            -
          </button>
          <input
            class="opacitySlider"
            type="range"
            min="0"
            max="100"
            step="10"
            v-model="state.layerOpacity"
            id="opacitySlider"
          />
          <button
            style="margin-right: -5px"
            title="Increase"
            class="leaflet-control-zoom-in"
            v-on:click="updateOpacity(state.layerOpacity + 10)"
          >
            +
          </button>
        </div>
      </div>
    </l-control>
    <!-- Zoom Control -->
    <l-control-zoom position="bottomright"></l-control-zoom>
    <!-- Time Series Control -->
    <l-control
      position="topright"
      :disableClickPropagation="true"
      class="leaflet-bar leaflet-control leaflet-control-custom time-series-control"
      v-if="years.length"
    >
      <div class="yearControl">
        <label
          class="yearControl__heading leaflet-control-custom__heading"
          for="yearsTimeSeries"
          >Years</label
        >
        <div class="yearControl__yearsSlider yearsSlider">
          <input
            class="yearsSlider__slider"
            type="range"
            list="yearsSlider"
            step="1"
            v-model="state.selectedYearIndex"
            :min="0"
            :max="years.length - 1"
            id="yearsTimeSeries"
          />
          <datalist id="yearsSlider" class="yearsSlider__values">
            <option
              class="yearsSlider__value"
              v-for="year in yearsReversed"
              ref="yearsSliderValueRef"
              v-on:click="yearsSliderLabelClick"
            >
              {{ year }}
            </option>
          </datalist>
        </div>
      </div>
    </l-control>
    <!-- Legend -->
    <l-control
      position="topleft"
      :disableClickPropagation="true"
      class="leaflet-bar leaflet-control leaflet-control-custom legend-box"
    >
      <div class="detailsBox">
        <span style="" class="legendTitle"> Legend </span>
        <div class="detailsWrapper">
          <div class="dashHeading">
            <img :src="progressIcon" height="20px" width="45px" alt="" />
          </div>
          <dl class="legendItems">
            <template v-for="legendLayer in state.legendLayers">
              <component
                :is="legendLayer.heading ? 'dt' : 'dd'"
                class="legendItem"
                :class="{
                  'legendItem--heading': legendLayer.heading,
                  'legendItem--headingHidden': legendLayer.hideHeading,
                }"
                v-if="
                  isZoomWithinScales(
                    legendLayer.minScale,
                    legendLayer.maxScale,
                    state.mapObject.getZoom()
                  )
                "
              >
                <img
                  v-if="legendLayer.img"
                  :src="legendLayer.img"
                  class="legendItem__icon"
                  width="20px"
                  height="20px"
                  alt=""
                />
                <span
                  class="legendItem__label"
                  :class="{ 'legendItem__label--heading': !legendLayer.img }"
                  >{{ legendLayer.label }}</span
                >
              </component>
            </template>
          </dl>
          <p
            v-if="
              clustered ||
              state.legendMaxZoomLevels.some(
                (l) => l > state.mapObject.getZoom()
              )
            "
            class="zoomDisclaimer"
          >
            (zoom in to see more detailed features)
          </p>
        </div>
      </div>
    </l-control>
  </l-map>
</template>

<script setup>
import {
  computed,
  markRaw,
  onMounted,
  nextTick,
  reactive,
  ref,
  watch,
} from "vue";
import { circleMarker, latLng, latLngBounds } from "leaflet";
import {
  LControl,
  LControlZoom,
  LGeoJson,
  LMap,
  LPopup,
  LRectangle,
} from "@vue-leaflet/vue-leaflet";
import { markerClusterGroup } from "leaflet.markercluster"; //needed for "esri-leaflet-cluster"
import { featureLayer } from "esri-leaflet-cluster";
import { request } from "esri-leaflet";
import { arcgisOnlineProvider } from "esri-leaflet-geocoder";
import LoadingIndicator from "./LoadingIndicator.vue";
import EBasemapLayer from "./esri/EBasemapLayer.vue";
import EDynamicMapLayer from "./esri/EDynamicMapLayer";
import EControlGeocoder from "./esri/EControlGeocoder";

import "leaflet/dist/leaflet.css";
import "esri-leaflet-geocoder/dist/esri-leaflet-geocoder.css";
import progressIcon from "../../images/chesapeake-progress-logo--icon-only.svg";

//template refs
const map = ref(null);
const yearsSliderValueRef = ref(null);
const overlayLayer = ref(null);
const features = ref(null);

//props
const props = defineProps({
  esriKey: {
    type: String,
    required: true,
  },
  dataSource: {
    type: String,
    required: true,
  },
  layersProp: {
    type: Array,
  },
  identificationLayers: {
    type: Array,
  },
  identificationFieldNames: {
    type: Array,
  },
  legendLayerIds: {
    type: Array,
  },
  years: {
    type: Array,
    default: [],
  },
  view: null,
  clusteredProp: null,
  highlightColor: null,
  layerOpacityProp: {
    type: Number,
    default: 90,
  },
  showOpacitySlider: {
    type: Boolean,
    default: false,
  },
  zoomScaleNoClustering: {
    type: Number,
    default: 11,
  },
  layerIsSustainableSchools: {
    type: Boolean,
    default: false,
  },
});

//data
const state = reactive({
  mapObject: null,
  loading: false, //flag for showing loading indicator
  bounds: null,
  mapOptions: {
    zoomControl: false,
  },
  geoJson: null,
  selectedYearIndex: props.years.length ? props.years.length - 1 : null, //default to the last one
  layerOpacity: props.layerOpacityProp,
  layerIsSegmentAttainment: false,
  popUpContent: "",
  progressIcon: progressIcon,
  legendLayers: [],
  legendMaxZoomLevels: [],
  geoJsonOptions: {
    pointToLayer: (feature, latLng) => {
      const highlightSymbol = {
        fillColor: props.highlightColor,
        color: props.highlightColor,
        weight: 5,
        opacity: 1,
        fillOpacity: 0.8,
      };
      if (feature.properties.TREND) highlightSymbol.radius = 8;
      else if (feature.properties.Condition || feature.properties.STATION_ID)
        highlightSymbol.radius = 10;
      else highlightSymbol.radius = 6;
      return circleMarker(latLng, highlightSymbol);
    },
  },
  geocoderProviders: [
    arcgisOnlineProvider({ apikey: props.esriKey, countries: ["USA"] }),
  ],
  usaSearchBounds: latLngBounds(latLng(20, -110), latLng(45, -70)),
  featureCollection: [],
});
const overlayClustered = ref(null);
const geoStyler = (feature) => ({
  color: props.highlightColor,
  width: 10,
  opacity: 1.0,
  fillOpacity: 0,
});

//lifecycle
onMounted(() => {
  getLegendLayers(props.dataSource + "/legend");
});

//methods
function onMapReady() {
  state.mapObject = map.value.leafletObject;
  //console.log(state.mapObject);
  const labelPane = state.mapObject.createPane("labels");
  labelPane.style.zIndex = "625";
  labelPane.style.pointerEvents = "none";

  //add overlay layer
  if (clustered.value) {
    //this doesn't work... waiting on a map that needs to use clustering to test
    overlayClustered.value = markRaw(
      featureLayer({
        url:
          props.dataSource.replace(/\/$/, "") +
          "/" +
          (props.identificationLayers.length > 0
            ? props.identificationLayers[0]
            : ""),
        zoomToBoundsOnClick: true,
        animate: false,
        animateAddingMarkers: false,
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: false,
        pointToLayer: function (geojson, latlng) {
          let iconColor = "#000";
          switch (geojson.properties.cbp_RANK) {
            case "EXCELLENT":
              iconColor = "#009AD3";
              break;
            case "GOOD":
              iconColor = "#47AE96";
              break;
            case "FAIR":
              iconColor = "#8DC35A";
              break;
            case "POOR":
              iconColor = "#796027";
              break;
            case "VERY_POOR":
              iconColor = "#630000";
              break;
            case "VERY POOR":
              iconColor = "#630000";
              break;
          }
          return circleMarker(latlng, {
            weight: 1,
            fill: true,
            color: "#000",
            fillOpacity: 1.0,
            fillColor: iconColor,
          }).setRadius(7);
        },
      })
    );
    overlayClustered.value.addTo(state.mapObject);
  }
}
function mapClick(e) {
  //Bind Popups
  const latLngClicked = e.latlng;
  if (
    clustered.value &&
    state.mapObject.getZoom() < props.zoomScaleNoClustering
  ) {
    const buffer = (1 / state.mapObject.getZoom()) * 0.05; // need to buffer inversely relative to zoom scale
    const latLngSW = latLng(
      latLngClicked.lat - buffer,
      latLngClicked.lng - buffer
    );
    const latLngNE = latLng(
      latLngClicked.lat + buffer,
      latLngClicked.lng + buffer
    );
    const latLngClickedBounds = latLngBounds(latLngSW, latLngNE);
    overlayClustered
      .query()
      .within(latLngClickedBounds)
      .run((error, featureCollection) => {
        state.geoJson = featureCollection.features[0];
      });
  } else {
    overlayLayer.value.leafletObject
      .identify()
      .on(state.mapObject)
      .at(latLngClicked)
      .tolerance(5)
      .layers("visible:" + props.identificationLayers.join(","))
      .run((error, featureCollection, response) => {
        if (error || featureCollection.features.length === 0) {
          state.geoJson = null;
          state.featureCollection = [];
        } else {
          //get only the same polygon across all layers (years)
          const selectedLayers = response.results.filter(
            (f) => f.value === response.results[0].value
          );
          const featureIds = selectedLayers.map((l) => l.attributes.OBJECTID);
          //filter the featureCollection to be the same polygon across all years
          state.featureCollection = featureCollection.features.filter((f) =>
            featureIds.includes(f.id)
          );
          //set the 'selected' one to be the right year
          const selectedFeature =
            state.featureCollection[
              state.featureCollection.length - 1 - state.selectedYearIndex
            ] ?? state.featureCollection[0];

          let popupContent = getPopupContent(
            selectedFeature,
            props.identificationFieldNames
          );
          state.geoJson = selectedFeature;
          if (popupContent !== "") {
            state.popUpContent = popupContent;
            features.value.leafletObject.openPopup(latLngClicked);
          }
        }
      });
  }
}
function updateOpacity(val) {
  state.layerOpacity = Math.min(Math.max(val, 0), 100);
}
function getLegendLayers(url) {
  state.legendLayers = [];
  state.legendMaxZoomLevels = [];
  request(url, {}, (error, response) => {
    if (error) {
      console.log(error);
    } else {
      const layers = response.layers;

      // if no legend layers were specified, just cycle through and output them in order with headings
      if (props.legendLayerIds.length === 0) {
        for (let j = 0; j < layers.length; j++) {
          let layer = layers[j];
          addLayerToLegend(layer, false);
        }
      } else {
        // otherwise, find each layer by ID and add it, conditionally showing heading
        props.legendLayerIds.forEach((legendLayerProp) => {
          let legendLayerId = legendLayerProp["layerId"];
          let hideHeading = legendLayerProp["hideHeading"];
          if (layers[legendLayerId]) {
            let layer = layers[legendLayerId];
            addLayerToLegend(layer, hideHeading);
          }
        });
      }
    }
  });
}
function addLayerToLegend(layer, hideHeading) {
  // add zoom breakpoint
  if (layer.maxScale !== 0) {
    state.legendMaxZoomLevels.push(getZoomFromScale(layer.maxScale));
  }

  // add heading
  state.legendLayers.push({
    maxScale: layer.maxScale,
    minScale: layer.minScale,
    label: layer.layerName,
    heading: true,
    hideHeading: hideHeading,
  });
  // add legend items
  const legend = layer.legend;
  for (let i = 0; i < legend.length; i++) {
    let legendItem = legend[i];

    state.legendLayers.push({
      maxScale: layer.maxScale,
      minScale: layer.minScale,
      img: "data:image/png;base64," + legendItem.imageData,
      label: legendItem.label || layer.layerName,
    });
  }
}
function getPopupContent(feature, fieldNames) {
  let popupContent = "";
  const featureAttributes = feature.properties;
  if (state.layerIsSegmentAttainment)
    popupContent += "<b>Segment: " + featureAttributes.SEGMENT + "</b><br/>";
  if (fieldNames.length > 0) {
    const fieldNameList = fieldNames;
    for (let i = 0; i < fieldNameList.length; i++) {
      const aliasAndName = fieldNameList[i].split("|");
      const fieldAlias = aliasAndName[0];
      let fieldName = fieldAlias;
      let popupFieldContent = "";
      if (aliasAndName.length === 2) fieldName = aliasAndName[1];
      let fieldValue = featureAttributes[fieldName];
      if (!fieldValue) fieldValue = featureAttributes[fieldAlias];
      fieldValue = String(fieldValue).trim();
      if (fieldValue.toUpperCase().indexOf("HTTP") === 0) {
        popupFieldContent =
          '<b><a target="_blank" href="' +
          fieldValue +
          '">Learn More</a></b><br/>';
      } else if (state.layerIsSegmentAttainment) {
        if (!isNaN(fieldValue)) {
          let fieldValueColor = "#EFEFEF";
          if (fieldValue === 100) fieldValueColor = "#0099D5";
          else if (fieldValue >= 95) fieldValueColor = "#42AF96";
          else if (fieldValue >= 85) fieldValueColor = "#8DC452";
          else if (fieldValue >= 75) fieldValueColor = "#796227";
          else if (fieldValue >= 0) fieldValueColor = "#640000";
          popupFieldContent =
            '<span style="background-color:' +
            fieldValueColor +
            '">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span> &nbsp; ' +
            fieldAlias +
            "</b>: " +
            fieldValue +
            "%" +
            "<br/>";
        }
      } else if (props.layerIsSustainableSchools) {
        if (fieldAlias === "School Name") {
          popupFieldContent =
            "<b>" + fieldAlias + "</b>: " + fieldValue + "<br/>";
        } else if (fieldValue === "x") {
          popupFieldContent = "<b>" + fieldAlias + "</b><br/>";
        }
      } else if (fieldValue !== "" && fieldValue !== "null") {
        popupFieldContent =
          "<b>" + fieldAlias + "</b>: " + fieldValue + "<br/>";
      }
      popupContent += popupFieldContent;
    }
  }
  return popupContent;
}
function isZoomWithinScales(minScale, maxScale, zoom) {
  //if maxScale/minScale is 0, there is no limit, so we can ignore it
  return (
    ((maxScale === 0 || zoom < getZoomFromScale(maxScale)) && minScale === 0) ||
    zoom > getZoomFromScale(minScale)
  );
}
//see https://github.com/Esri/esri-leaflet/issues/426#issuecomment-459169743
function getScaleFromZoom(zoom) {
  return 591657550.5 / Math.pow(2, zoom);
}
function getZoomFromScale(scale) {
  return Math.log(591657550.5 / scale) / Math.log(2);
}
function geosearchOnResult(data) {
  const geocodeResult = data.results[0];
  if (geocodeResult) {
    state.mapObject.setView(geocodeResult.latlng, 13, { animate: true });
  }
}
function yearsSliderLabelClick(e) {
  let yearLabelsArray = [...yearsSliderValueRef.value].reverse();
  state.selectedYearIndex = yearLabelsArray.indexOf(e.target);
}

//computed
const zoom = computed(() => {
  return props.view === "bay" ? 8 : 6;
});
const center = computed(() => {
  return props.view === "bay" ? latLng(38.2, -76.5) : latLng(40, -77.1);
});
const clustered = computed(() => {
  return props.clusteredProp === "clustered";
});
const overlayLayerTimeRange = computed(() => {
  if (!state.selectedYearIndex) return [null, null]; //return null for 'to' and 'from' if a time series wasn't provided
  let selectedYear = props.years[state.selectedYearIndex];
  if (selectedYear.includes("-")) {
    let yearArray = selectedYear.split("-");
    return [
      new Date(Number(yearArray[0]) + "-01-01T00:00:00.000-05:00"),
      new Date(Number(yearArray[1]) + "-12-31T00:00:00.000-05:00"),
    ];
  } else {
    return [
      new Date(Number(selectedYear) + "-01-01T00:00:00.000-05:00"),
      new Date(Number(selectedYear) + "-12-31T00:00:00.000-05:00"),
    ];
  }
});
const yearsReversed = computed(() => {
  return props.years.slice().reverse();
});

//watchers
watch(
  () => state.selectedYearIndex,
  () => {
    if (!state.featureCollection.length) {
      return;
    }

    state.popUpContent = getPopupContent(
      state.featureCollection[
        state.featureCollection.length - 1 - state.selectedYearIndex
      ] ?? state.featureCollection[0],
      props.identificationFieldNames
    );
  }
);
</script>

<style lang="scss" scoped>
@import "../../styles/variables.scss";
.leaflet-control-custom {
  background-color: $white;
  overflow-x: hidden;
  overflow-y: hidden;
  border-radius: 0.5em;
  padding: 1em;
}
.leaflet-control-custom__heading {
  font-weight: $bold-weight;
  color: $type;
}
.detailsWrapper {
  border-bottom: 2px solid $gray;
}
.legend-box {
  min-width: 200px;
  max-width: 310px;
  font-family: $font-family;
  font-size: 16px;
}
.legendTitle {
  margin-bottom: 0.6em;
  text-align: center;
  font-size: 1.3em;
  font-weight: $bold-weight;
  width: 100%;
  color: $type;
  display: flex;
  justify-content: center;
}
.dashHeading {
  --dashColor: #{$gray};
  font-size: 5px;
}
.legendItems {
  margin: 1em 0;
  padding-inline-start: 0;
}
.legendItem {
  display: flex;
  align-items: center;
  gap: 0.5em;
  margin-inline-start: 0;
  line-height: 1.3;

  & + & {
    margin-block-start: 0.2em;
  }
}
.legendItem + .legendItem--heading {
  margin-block-start: 0.8em;
}
.legendItem--headingHidden {
  visibility: hidden;
  height: 0;
}
.legendItem__icon {
  flex: 0 1 auto;
}
.legendItem__label--heading {
  font-weight: $bold-weight;
}
.zoomDisclaimer {
  font-size: 0.9em;
  color: $gray--reading;
}

.opacityBox {
  text-align: center;
}
.opacityBox__heading {
  font-weight: $bold-weight;
  color: $type;
}
.opacityBox__controls {
  display: flex;
  gap: 0.5em;
}

.yearControl {
  text-align: center;
}
.yearsSlider {
  display: flex;
  margin-block-start: 0.5em;
}
.yearsSlider__slider {
  width: 2em;
  border-radius: 5px;
  background: #ccc;
  outline: none;
  writing-mode: bt-lr; /* IE */
  -webkit-appearance: slider-vertical; /* WebKit */
}
.yearsSlider__values {
  list-style: none;
  margin: 0;
  padding: 0 0 0 0.25em;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.yearsSlider__value {
  padding: 5px 0;
  cursor: pointer;
}

.loading-enter-active {
  transition: opacity 0.2s;
  transition-delay: 0.5s; //add delay so that loading icon doesn't display for a half second load request
}
.loading-leave-active {
  transition: opacity 0.2s;
}
.loading-enter, .loading-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
<style lang="scss">
.leaflet-right.leaflet-top {
  display: flex;
  align-items: start;
  flex-wrap: wrap;
  max-width: 266px;
  justify-content: flex-end;
  gap: 0.5em;
  margin: 1em 1em 0 0;

  .leaflet-control {
    float: none;
    margin: 0;
  }
}
.leaflet-touch .leaflet-bar a {
  width: 35px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.time-series-control {
  z-index: 0;
}
.geocoder-control {
  height: 34px;
}
.geocoder-control-suggestions {
  z-index: 1;
}
</style>
